import { useEffect, useState } from 'react';

import { apiClientFetchSynonymSearchApiKey } from '#/api/apiClientFetchSynonymSearchApiKey';
import type { ApiISODateTimeString } from '#/api/types/ApiTypedDate';
import { AlgoliaSearchSelect } from '#/components/AlgoliaSearchSelect/AlgoliaSearchSelect';
import { Field } from '#/components/Form/Field/Field';
import { FieldDescription } from '#/components/Form/Field/FieldDescription';
import { FieldLabel } from '#/components/Form/Field/FieldLabel';
import type { FormUsageContext } from '#/components/Form/Form';
import { Form } from '#/components/Form/Form';
import { Icon } from '#/components/Icon/Icon';
import { useI18n } from '#/i18n/hooks/useI18n';
import { salariesUpdateCareerPathSynonyms } from '#/modules/salaries/api/salariesUpdateCareerPathSynonyms';
import type { CareerPathAnalyticsData } from '#/modules/salaries/types/CareerPathAnalyticsData';
import type { EstimatedRange } from '#/modules/salaries/types/EstimatedRange';
import { getSearchAlgoliaConfigWithApiKey } from '#/modules/search/algolia/searchAlgoliaConfig';
import { AlgoliaConfig } from '#/utils/algolia/AlgoliaConfig';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { trackEvent } from '#/utils/analytics/track/trackEvent';

import {
  AddTitlesIconContainer,
  CareerPathFormContainer,
  CareerPathFormText,
  Container,
  JobTitleFieldContainer,
  StyledPrimaryButton,
} from './CareerPathForm.styled';

type CareerPathFormProps = {
  analyticsTitle: string;
  usageContext: FormUsageContext;
  analyticsData: CareerPathAnalyticsData;
  careerPathData: EstimatedRange[];
  salaryModelCreated: ApiISODateTimeString;
  setCareerPathData: (careerPath: EstimatedRange[]) => void;
  city?: string;
  selectedSynonymId: string;
  stateCode?: string;
  searchApiKey: string | null;
  setSearchApiKey: (arg0: string) => void;
  yearsOfExperience?: number;
};

export function CareerPathForm({
  analyticsTitle,
  usageContext,
  analyticsData,
  careerPathData,
  setCareerPathData,
  salaryModelCreated,
  selectedSynonymId,
  city,
  stateCode,
  yearsOfExperience,
  searchApiKey,
  setSearchApiKey,
}: CareerPathFormProps) {
  const { i18n } = useI18n();

  const [initialized, setInitialized] = useState(false);
  const [transitioned, setTransitioned] = useState(false);
  useEffect(() => {
    setInitialized(false);
  }, [city, setInitialized, selectedSynonymId, stateCode, yearsOfExperience]);

  useEffect(() => {
    setTransitioned(false);
  }, [city, setInitialized, selectedSynonymId, stateCode, yearsOfExperience]);

  const initializeCareerPathForm = () => {
    setInitialized(true);
    trackClicked('Career Path Add Title', analyticsData);
  };

  const initialValues = {};
  const onTransitionEnd = ({ propertyName }: { propertyName: string }) => {
    if (propertyName === 'clip-path') {
      setTransitioned(true);
    }
  };
  const onSubmit = async ({
    handleError,
    updateValues,
    updateSubmitting,
    values,
  }: // oxlint-disable-next-line typescript/no-explicit-any
  any) => {
    if (!values.additionalSynonymId) {
      updateSubmitting(false);
      return;
    }

    const updatedSynonymIds = [
      ...careerPathData
        .filter((cp) => cp.synonymId !== selectedSynonymId)
        .map((cp) => cp.synonymId),
      values.additionalSynonymId,
    ];

    const careerPath = await salariesUpdateCareerPathSynonyms({
      selectedSynonymId,
      city,
      stateCode,
      yearsOfExperience,
      currentSynonymIds: updatedSynonymIds,
      synonymIdToAdd: values.additionalSynonymId,
    });
    // Mimic form error when a null career path value exists
    if (careerPath.find((career) => career.salary === null)) {
      const error = {
        responseJson: {
          errors: [
            {
              type: 'not_enough_data',
              name: 'synonymId',
              description: ' ', // @HACK: Blank space keeps error as truthy w/o displaying text
            },
          ],
        },
      };
      handleError(error);
      updateSubmitting(false);
    } else {
      setCareerPathData(careerPath);
      updateValues({
        additionalSynonymName: '',
        additionalSynonymId: '',
        additionalSynonymStandardName: '',
        synonymId: '',
      });
      updateSubmitting(false);
      trackEvent('Career Path Success Added Title', {
        ...analyticsData,
        job_titles_displayed: careerPath.map((estimatedRange) => ({
          synonym_name: estimatedRange.synonymName,
          standard_name: estimatedRange.jobTitleName,
        })),
        job_title_added: {
          synonym_name: values.additionalSynonymName,
          standard_name: values.additionalSynonymStandardName,
        },
      });
    }
  };

  return (
    <Form
      analyticsTitle={analyticsTitle}
      usageContext={usageContext}
      qaId="career-path-chart-form"
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ errors, clearErrors, submitForm }) => (
        <Container>
          <CareerPathFormContainer
            $hasError={Boolean(errors.synonymId)}
            $initialized={initialized}
            $transitioned={transitioned}
            onTransitionEnd={onTransitionEnd}
          >
            <CareerPathFormText>
              <FieldLabel>
                {errors.synonymId
                  ? i18n('Oops! Try another title')
                  : i18n('Add your own titles')}
              </FieldLabel>
              <FieldDescription>
                {errors.synonymId
                  ? i18n(
                      "We don't currently have enough data to add this title to your career path.",
                    )
                  : i18n('Start typing to see suggestions')}
              </FieldDescription>
            </CareerPathFormText>
            <JobTitleFieldContainer>
              <Field
                name="synonymId"
                inputProps={{ searchApiKey }}
                renderInput={({ qaId }) => (
                  <AlgoliaSearchSelect<{
                    objectID: string;
                    name: string;
                    jobTitle: { name: string };
                  }>
                    qaId={qaId}
                    onChange={(newOption) => {
                      if (!newOption) return;

                      clearErrors(['selectedSynonymId']);
                      submitForm({
                        additionalSynonymId: newOption.value.objectID,
                        additionalSynonymName: newOption.value.name,
                        additionalSynonymStandardName:
                          newOption.value.jobTitle.name,
                      });
                    }}
                    searchArgs={{
                      config: searchApiKey
                        ? getSearchAlgoliaConfigWithApiKey(searchApiKey)
                        : undefined,
                      ...AlgoliaConfig.SALARY_REPORT_SURVEY_TITLES,
                      options: {
                        ...AlgoliaConfig.SALARY_REPORT_SURVEY_TITLES.options,
                        filters: `includeInSurvey:true AND jobTitle.created < ${salaryModelCreated}`,
                      },
                      onApiKeyTimeout: () =>
                        apiClientFetchSynonymSearchApiKey().then(
                          (newApiKey) => {
                            setSearchApiKey(newApiKey);
                            return newApiKey;
                          },
                        ),
                    }}
                    getOptionLabel={(option) => option.value.name}
                  />
                )}
              ></Field>
            </JobTitleFieldContainer>
          </CareerPathFormContainer>
          <StyledPrimaryButton
            type="button"
            variant="primary"
            qaId="career-path-chart-form-button"
            $initialized={initialized}
            $transitioned={transitioned}
            onClick={() => initializeCareerPathForm()}
          >
            <AddTitlesIconContainer>
              {i18n('Add Titles')}
              <Icon name="plus" size={16} />
            </AddTitlesIconContainer>
          </StyledPrimaryButton>
        </Container>
      )}
    />
  );
}
